'use client';

import { useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { FloatingDock } from '@/components/dock';
import { SubpoenaNotificationBanner } from '@/components/legal';
import { UpgradeBanner } from '@/components/subscription/UpgradeBanner';
import { NewPricingBanner } from '@/components/subscription/NewPricingBanner';
import { PaymentFailedBanner } from '@/components/subscription/PaymentFailedBanner';
import { MFASetupBanner } from '@/components/mfa';
import { TrialBanner } from '@/components/TrialBanner';
import { MilestoneTracker } from '@/components/upsell/MilestoneTracker';
import { ProductTour } from '@/components/ProductTour';
import { useActivationTracking } from '@/hooks/useActivationTracking';
import { useExperiencePreset } from '@/hooks/useFeature';
import { ContainerBootSplash } from '@/components/ContainerBootSplash';

const SIDEBAR_COLLAPSED_KEY = 'settlewise-sidebar-collapsed';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();

  // Persist sidebar state
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Passive activation tracking — fires user_activated event once when criteria met
  useActivationTracking();

  // Hide floating Sage chat on Ask Sage page (redundant), admin routes, and Sibyl pages (has own advisor panel)
  /** The firm's own workspace, as opposed to a client's. */
  const isAdvisorSurface = location.pathname.startsWith('/advisor') || location.pathname.includes('/advisor/');

  const hideSageChat = location.pathname.includes('/ask-sage') || location.pathname.includes('/admin') || location.pathname.includes('/sibyl') || location.pathname.includes('/redline');

  // The collector preset (#1747) is one screen with its own agent surface (the extension) and its
  // own entitlement model (W3). None of the B2C chrome applies: no onboarding-checklist/Sage dock,
  // product tour, milestone toasts, or subscription/trial banners. The dock also floats over the
  // Collection page's primary actions, so it must not mount there.
  const isCollector = useExperiencePreset() === 'collector';

  // Load sidebar state from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved === 'true') {
      setCollapsed(true);
    }
  }, []);

  useEffect(() => {
    if (mounted) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    }
  }, [collapsed, mounted]);

  // The iii Agent Hub "Talk with us" widget is intentionally NOT loaded in the
  // authenticated experience — it's a public/marketing entry point and is noise
  // (and a privacy concern) once a user is signed in.

  return (
    <TooltipProvider>
      <div className="min-h-dvh h-dvh bg-slate-50 flex overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:flex min-h-dvh h-dvh flex-shrink-0 overflow-visible">
          <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
        </div>

        {/* Mobile Sidebar Drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="!w-[min(16rem,85vw)] !max-w-[min(16rem,85vw)] p-0">
            <Sidebar collapsed={false} onToggle={() => setMobileOpen(false)} onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 max-w-full overflow-x-hidden relative z-0">
          <Header showMenuButton onMenuClick={() => setMobileOpen(true)} />

          {/* Payment Failed Banner - highest priority, non-dismissible */}
          <PaymentFailedBanner />

          {/* Trial Countdown Banner */}
          {!isCollector && <TrialBanner />}

          {/* New Pricing Announcement Banner */}
          {!isCollector && <NewPricingBanner />}

          {/*
            MFA Setup Banner. NOT on firm surfaces (#1853): a partner who reads "your account is at greater
            risk" on every screen of the product they just bought draws the opposite of the intended
            conclusion. The fact itself is already modelled in src/lib/setup/setupState.ts, where it is
            counted as one setup item and never blocking — that is where a firm should meet it.
          */}
          {!isAdvisorSurface && <MFASetupBanner />}

          {/* Upgrade Banner for Free Users */}
          {!isCollector && <UpgradeBanner />}

          {/* Legal Process Alert Banner */}
          <SubpoenaNotificationBanner className="mx-4 mt-4" />

          <main className="flex-1 overflow-y-auto overflow-x-hidden">{children}</main>
        </div>

        {/* Milestone achievement tracker — fires toast nudges */}
        {!isCollector && <MilestoneTracker />}

        {/* Guided product tour for first-time users */}
        {!isCollector && <ProductTour />}

        {/* Container boot splash — shown when AI container is starting up */}
        <ContainerBootSplash />

        {/* Floating Dock - Onboarding checklist + Sage AI chat */}
        {!isCollector && <FloatingDock hideSage={hideSageChat} />}
      </div>
    </TooltipProvider>
  );
}

export default AppLayout;
